import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { CandidateAtsFileFragment, DeleteAtsFileDocument } from 'generated/resources/graphql'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CurriculumViewer } from 'modules/ats/components/CurriculumViewer'
import { CvDropzone } from 'modules/ats/components/CvDropzone'
import { useCandidateFiles } from 'modules/ats/hooks/candidateFiles'
import { timeAgoWhenRecent } from 'modules/ats/lib/dates'

interface Props {
  candidateId: string
}

export const SectionContent = ({ candidateId }: Props) => {
  const { loadingFiles, candidateCvFiles } = useCandidateFiles({
    atsCandidateId: candidateId,
  })

  if (loadingFiles) return null

  const cvFile = candidateCvFiles.find((file) => file.atsApplicationId === null)

  if (cvFile?.id) {
    const attachment = cvFile.preview ? cvFile.preview : cvFile.file

    return (
      <Box width="full" gap="s24">
        <CurriculumViewer
          url={attachment.url}
          contentType={attachment.contentType}
          filename={attachment.filename}
        />
        <Box paddingY="s24" paddingX="s20">
          <CurriculumItem cv={cvFile} />
        </Box>
      </Box>
    )
  }

  const applicationCv = candidateCvFiles[0]

  // This section should be refactored so that the component has only one return statement.
  if (applicationCv) {
    const attachment = applicationCv.preview ? applicationCv.preview : applicationCv.file

    return (
      <Box width="full" gap="s24">
        <CurriculumViewer
          url={attachment.url}
          contentType={attachment.contentType}
          filename={applicationCv.file.filename}
        />
        <Box paddingY="s24" paddingX="s20">
          <UploadCandidateCv candidateId={candidateId} />
        </Box>
      </Box>
    )
  }

  return (
    <Box width="full" gap="s24">
      <Text.Normal color="textPrimary" weight="semibold">
        {i18n.t('ats.candidate_details.cv.title')}
      </Text.Normal>

      <UploadCandidateCv candidateId={candidateId} />
    </Box>
  )
}

function UploadCandidateCv({ candidateId }: { candidateId: string }) {
  const { bulkCreateCandidateFiles, creatingFiles } = useCandidateFiles({
    atsCandidateId: candidateId,
  })

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const onDrop = async (files: File[] = []) => {
    if (!files[0]) {
      return
    }

    await bulkCreateCandidateFiles({
      authorId: accessId,
      atsCandidateId: candidateId,
      resourceId: candidateId,
      authorType: 'access',
      resourceType: 'candidate',
      files: files.map((file) => {
        return { attachment: file, category: 'cv' }
      }),
    })
  }

  return (
    <Box gap="s40">
      <CvDropzone onDrop={onDrop} disabled={creatingFiles} />
    </Box>
  )
}

interface CurriculumItemProps {
  cv: CandidateAtsFileFragment
}

const CurriculumItem = ({ cv }: CurriculumItemProps) => {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [deleteAtsFile, { loading }] = useMutation(DeleteAtsFileDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsFile,
  })

  const onDelete = async () => {
    const result = await deleteAtsFile({
      variables: { id: cv.id },
      update(cache) {
        cache.evict({ id: cache.identify(cv) })
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
      setDeleteModalOpen(false)
    }

    return handler
  }

  return (
    <>
      <Box
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{ all: 'abs008' }}
        paddingX="s24"
        paddingY="s16"
      >
        <Box
          flexGrow
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          width="full"
          gap="s16"
        >
          <Box alignItems="center" flexGrow flexDirection="row" gap="s16">
            <DocumentType mimetype={cv.file.contentType} />
            <Box gap="s4">
              <Text.Normal weight="medium" ellipsis overflowWrap>
                {cv.file.filename}
              </Text.Normal>
              <Text.Normal size="200" color="grey600">
                {timeAgoWhenRecent(cv.createdAt)}
              </Text.Normal>
            </Box>
          </Box>

          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative
                type="secondary"
                aria-label={i18n.t('ats.candidates.candidate_docs.more_actions')}
                iconName="ellipsis"
              />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="end" disablePortal>
              <PopoverMenu.HrefOption
                href={cv.file.url}
                title={i18n.t('documents.download')}
                download
                target="_blank"
              />
              <PopoverMenu.TouchableOption
                onClick={() => setDeleteModalOpen(true)}
                title={i18n.t('documents.delete')}
                danger
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      </Box>

      {deleteModalOpen && (
        <Modal
          illustration="delete"
          title={i18n.t('documents.confirm_delete.title')}
          description={i18n.t('documents.confirm_delete.description')}
          onClose={() => setDeleteModalOpen(false)}
          onSubmit={onDelete}
          footer={
            <Box flexDirection="row" gap="s8">
              <Button type="secondary" disabled={loading} onClick={() => setDeleteModalOpen(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button type="negative" disabled={loading} submit>
                {i18n.t('common.delete')}
              </Button>
            </Box>
          }
        />
      )}
    </>
  )
}
